import type { GetFormDefinitionResponse } from '@/api/form/v1/form_pb'
import type { Section1Values } from './sections/Section1'
import type { VisibilityCondition } from './fieldRules'

export type DynamicFieldType = 'text' | 'number' | 'email' | 'select' | 'boolean'

export interface DynamicFieldDefinition {
	key: string
	label: string
	section: string
	type: DynamicFieldType
	required: boolean
	options: string[]
	// When the field appears, if it depends on another answer.
	visibleWhen?: VisibilityCondition
	// Whether the field is asked of the first buyer only.
	firstBuyerOnly: boolean
}

export interface GeneratedFormDefinition {
	formId: bigint
	formName: string
	version: number
	city: string
	status: number
	submittedAt: string
	buyerFirstName: string
	buyerLastName: string
	buyerEmail: string
	buyerPhone: string
	section1: Section1Values
	dynamicFields: DynamicFieldDefinition[]
	dynamicValues: Record<string, string>
	prefill: Record<string, unknown>
}

type JsonMap = Record<string, unknown>

const SECTION1_KEY_SET = new Set<string>([
	'accommodationType',
	'rooms',
	'residenceType',
	'managedResidenceType',
	'lotNumber',
	'area',
	'parking',
	'usage',
	'vat',
	'price',
])

function parseJSONMap(input: string | undefined): JsonMap {
	if (!input) return {}
	try {
		const parsed = JSON.parse(input)
		if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
			return parsed as JsonMap
		}
		return {}
	} catch {
		return {}
	}
}

function toStringValue(value: unknown): string {
	if (value === undefined || value === null) return ''
	return String(value)
}

function normalizeBooleanValue(value: unknown): string {
	const normalized = toStringValue(value).trim().toLowerCase()
	if (normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'oui') {
		return 'true'
	}
	if (normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'non') {
		return 'false'
	}
	return ''
}

function getSection1Values(prefill: JsonMap): Section1Values {
	return {
		accommodationType: toStringValue(prefill.accommodationType),
		rooms: toStringValue(prefill.rooms),
		residenceType: toStringValue(prefill.residenceType),
		managedResidenceType: toStringValue(prefill.managedResidenceType),
		lotNumber: toStringValue(prefill.lotNumber),
		area: toStringValue(prefill.area),
		parking: toStringValue(prefill.parking),
		usage: toStringValue(prefill.usage),
		vat: toStringValue(prefill.vat),
		price: toStringValue(prefill.price),
	}
}

function getVisibleWhen(fieldDef: JsonMap): VisibilityCondition | undefined {
	const condition = fieldDef.visibleWhen
	if (!condition || typeof condition !== 'object' || Array.isArray(condition)) return undefined
	const { key, in: values } = condition as JsonMap
	if (typeof key !== 'string' || key === '' || !Array.isArray(values)) return undefined
	return { key, in: values.filter((value): value is string => typeof value === 'string') }
}

function getFieldType(fieldDef: JsonMap): DynamicFieldType {
	const enumValues = Array.isArray(fieldDef.enum) ? fieldDef.enum : []
	if (enumValues.length > 0) return 'select'

	const fieldType = toStringValue(fieldDef.type)
	if (fieldType === 'number' || fieldType === 'integer') return 'number'
	if (fieldType === 'boolean') return 'boolean'
	if (fieldType === 'string' && toStringValue(fieldDef.format) === 'email') return 'email'
	return 'text'
}

export function generateFormDefinition(
	response: GetFormDefinitionResponse,
): GeneratedFormDefinition {
	const schema = parseJSONMap(response.schemaJson)
	const prefill = parseJSONMap(response.prefillJson)

	const sections = Array.isArray(schema.sections) ? (schema.sections as JsonMap[]) : []
	const fields: DynamicFieldDefinition[] = []
	const values: Record<string, string> = {}
	const seenKeys = new Set<string>()

	const addField = (fieldDef: JsonMap, sectionTitle: string) => {
		const key = toStringValue(fieldDef.key)
		if (!key || seenKeys.has(key) || SECTION1_KEY_SET.has(key)) return
		seenKeys.add(key)

		const enumValues = Array.isArray(fieldDef.enum)
			? fieldDef.enum
					.filter((item): item is string => typeof item === 'string')
					.map(item => item.trim())
					.filter(item => item !== '')
			: []
		const type = getFieldType(fieldDef)

		fields.push({
			key,
			label: toStringValue(fieldDef.title) || key,
			section: sectionTitle,
			type,
			required: fieldDef.required === true,
			options: enumValues,
			visibleWhen: getVisibleWhen(fieldDef),
			firstBuyerOnly: fieldDef.firstBuyerOnly === true,
		})
		values[key] =
			type === 'boolean' ? normalizeBooleanValue(prefill[key]) : toStringValue(prefill[key])
	}

	for (const section of sections) {
		const sectionTitle = toStringValue(section.title) || ''

		if (
			section.variants &&
			typeof section.variants === 'object' &&
			!Array.isArray(section.variants)
		) {
			const variants = section.variants as JsonMap
			const variantKey = toStringValue(variants.key)
			const variantOptions = Array.isArray(variants.options)
				? (variants.options as JsonMap[])
				: []

			// Emit a field for the variant discriminator (e.g. buyerType) with its option values
			if (variantKey && !seenKeys.has(variantKey)) {
				seenKeys.add(variantKey)
				fields.push({
					key: variantKey,
					label: toStringValue(section.title) || variantKey,
					section: sectionTitle,
					type: 'select',
					required: false,
					options: variantOptions.map(opt => toStringValue(opt.value)).filter(v => v !== ''),
					firstBuyerOnly: false,
				})
				values[variantKey] = toStringValue(prefill[variantKey])
			}

			// Collect fields from all variants (deduped)
			for (const option of variantOptions) {
				const optionFields = Array.isArray(option.fields) ? (option.fields as JsonMap[]) : []
				for (const field of optionFields) {
					addField(field, sectionTitle)
				}
			}
		} else if (Array.isArray(section.fields)) {
			for (const field of section.fields as JsonMap[]) {
				addField(field, sectionTitle)
			}
		}
	}

	return {
		formId: response.formId,
		formName: response.formName,
		version: response.version,
		city: response.city,
		status: response.status,
		submittedAt: response.submittedAt,
		buyerFirstName: response.buyerFirstName,
		buyerLastName: response.buyerLastName,
		buyerEmail: response.buyerEmail,
		buyerPhone: response.buyerPhone,
		section1: getSection1Values(prefill),
		dynamicFields: fields,
		dynamicValues: values,
		prefill,
	}
}
